import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { VISTA_DE_RUTA, primerDestinoVisible, rutaDe, type Vista } from "./lib/nav";
import { ERROR_ENLACE, TIPO_ENLACE } from "./lib/supabase";
import { enSubdirectorio } from "./lib/rutas";
import Login from "./Login";
import CrearPassword from "./CrearPassword";
import { Shell } from "./components/Shell";
import { EsqueletoTiles } from "./components/Base";
import ErrorBoundary from "./components/ErrorBoundary";
import { TelonEntrada, BANDERA_ENTRADA } from "./components/TelonEntrada";
import { leerTokenTele } from "./oficina/oficina";

// La tele de la Oficina de agentes: pantalla completa, fuera del Shell.
const OficinaTele = lazy(() => import("./views/OficinaTele"));
// ¿Se abrió la tele con su enlace de dispositivo (#t=…)? Se decide una vez, al cargar:
// una tele no inicia sesión (las sesiones del proyecto caducan a las 12 h).
const TOKEN_TELE =
  typeof location !== "undefined" && location.pathname.startsWith("/oficina/tv") ? leerTokenTele() : null;

function TeleConSesion() {
  const { puedeVer } = useAuth();
  if (!puedeVer("oficina")) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={null}>
      <OficinaTele />
    </Suspense>
  );
}

// Vistas ya portadas. Las que faltan caen en PendientePortar, así el shell
// funciona completo desde el primer día y la migración avanza vista por vista.
const PORTADAS: Partial<Record<Vista, React.LazyExoticComponent<React.ComponentType>>> = {
  hoy: lazy(() => import("./views/Hoy")),
  resumen: lazy(() => import("./views/Resumen")),
  embudo: lazy(() => import("./views/Embudo")),
  atribucion: lazy(() => import("./views/Atribucion")),
  tiktok: lazy(() => import("./views/Tiktok")),
  google_ads: lazy(() => import("./views/GoogleAds")),
  chatgpt_ads: lazy(() => import("./views/ChatgptAds")),
  canales_comparativa: lazy(() => import("./views/CanalesComparativa")),
  marketing_resumen: lazy(() => import("./views/MarketingResumen")),
  klaviyo: lazy(() => import("./views/Klaviyo")),
  campanas_ghl: lazy(() => import("./views/CampanasGhl")),
  seo: lazy(() => import("./views/Seo")),
  reputacion: lazy(() => import("./views/Reputacion")),
  conmutador: lazy(() => import("./views/Conmutador")),
  abandonos: lazy(() => import("./views/Abandonos")),
  talleres: lazy(() => import("./views/Talleres")),
  higiene: lazy(() => import("./views/Higiene")),
  ml_bot: lazy(() => import("./views/MlBot")),
  bot_espejo: lazy(() => import("./views/BotEspejo")),
  "clientes-pareto": lazy(() => import("./views/ClientesPareto")),
  "clientes-recurrentes": lazy(() => import("./views/ClientesRecurrentes")),
  marketplaces: lazy(() => import("./views/Marketplaces")),
  tiendas: lazy(() => import("./views/Tiendas")),
  comunidad: lazy(() => import("./views/Comunidad")),
  "clientes-riesgo": lazy(() => import("./views/ClientesRiesgo")),
  "clientes-dormidos": lazy(() => import("./views/ClientesDormidos")),
  oficina: lazy(() => import("./views/Oficina")),
  proximamente: lazy(() => import("./views/Proximamente")),
  parrilla: lazy(() => import("./views/Parrilla")),
  vendedores: lazy(() => import("./views/Vendedores")),
  usuarios: lazy(() => import("./views/Usuarios")),
  operacion: lazy(() => import("./views/Operacion")),
  calidad: lazy(() => import("./views/Calidad")),
  perfil: lazy(() => import("./views/MiPerfil")),
  compras_slp: lazy(() => import("./views/ComprasSlp")),
  espejo_ventas: lazy(() => import("./views/EspejoVentas")),
  marketplaces_abasto: lazy(() => import("./views/Abasto")),
  metas_ventas: lazy(() => import("./views/MetasVentas")),
};

function PendientePortar({ vista }: { vista: string }) {
  return (
    <section className="card">
      <h3>Vista pendiente de migrar</h3>
      <p className="vacio">
        <b>{vista}</b> todavía no se porta a la beta. Está disponible y funcionando
        en el Radar actual.
      </p>
      {/* Este escape solo sirve mientras el Radar vanilla siga en la raíz.
          Después del switch la app nueva ES la raíz y el enlace daría vueltas. */}
      {enSubdirectorio && (
        <p style={{ marginTop: 12 }}>
          <a href="/" className="btn-sec">Abrir el Radar actual →</a>
        </p>
      )}
    </section>
  );
}

/**
 * A dónde entra alguien que llega a "/" (o a una ruta que no existe): al primer
 * destino que su rol sí puede ver, no siempre a Resumen — hay roles sin permiso
 * de Resumen y ahí aterrizaban en una pantalla de "Sin acceso". "Mi perfil" es
 * el último recurso: es la única vista que todos pueden ver.
 */
function useInicio(): Vista {
  const { puedeVer } = useAuth();
  return primerDestinoVisible(puedeVer) ?? "perfil";
}

function Inicio() {
  const destino = useInicio();
  return <Navigate to={`/${rutaDe(destino)}`} replace />;
}

function Pagina() {
  const { vista } = useParams();
  const { puedeVer } = useAuth();
  const inicio = useInicio();
  // la URL trae la ruta legible (google-ads); adentro seguimos usando la llave
  // de siempre (google_ads), que es la misma de los permisos en la base
  const v = VISTA_DE_RUTA[vista ?? "resumen"];

  if (!v) return <Navigate to="/" replace />;
  // si entraron con la forma vieja, se les corrige la URL sin perder la vista
  if (vista && vista !== rutaDe(v)) return <Navigate to={`/${rutaDe(v)}`} replace />;
  if (!puedeVer(v)) {
    return (
      <Shell vista={v}>
        <section className="card">
          <h3>Sin acceso</h3>
          <p className="vacio">Tu rol no tiene permiso para ver esta sección.</p>
          <p style={{ marginTop: 12 }}>
            {/* .btn-chat es la pastilla de enlace que ya existe en app.css */}
            <Link to={`/${rutaDe(inicio)}`} className="btn-chat">Ir a mi inicio →</Link>
          </p>
        </section>
      </Shell>
    );
  }

  const Comp = PORTADAS[v];
  return (
    <Shell vista={v}>
      {/* El boundary va por VISTA y con key: si una pantalla truena, el menú y
          los filtros siguen ahí y basta cambiar de pestaña para salir. */}
      <ErrorBoundary key={v}>
        <Suspense fallback={<div className="tiles"><EsqueletoTiles n={5} /></div>}>
          {Comp ? <Comp /> : <PendientePortar vista={v} />}
        </Suspense>
      </ErrorBoundary>
    </Shell>
  );
}

// El enlace del correo se lee UNA vez, a nivel de módulo (ver lib/supabase.ts):
// para cuando App renderiza, supabase-js ya se llevó el hash de la URL.
const ES_ENLACE_CORREO = TIPO_ENLACE === "invite" || TIPO_ENLACE === "recovery";
const TEXTO_ENLACE_VENCIDO =
  "Ese enlace ya no es válido: los enlaces del correo caducan y solo sirven una vez. " +
  "Escribe tu correo y pide uno nuevo con «¿Olvidaste tu contraseña?».";

export default function App() {
  const { session, perfil, cargando, errorPerfil } = useAuth();
  // se enciende al guardar la contraseña: de ahí en adelante App sigue su
  // camino normal (ya hay sesión) en vez de volver a pedirla
  const [passGuardada, setPassGuardada] = useState(false);

  // El telón se enciende cuando LLEGA una sesión con la bandera que dejó el
  // login — un useEffect sobre session, exactamente como PILOT (App.jsx 115).
  // OJO, esto estuvo roto: antes era un useState con initializer, que solo se
  // evalúa al MONTAR App. Pero en un login real App ya está montada mostrando
  // el formulario cuando la bandera se pone, así que el initializer nunca la
  // veía y la palomita jamás salía. Solo funcionaba recargando la página —
  // que era justo como se probó, por eso el bug se escapó dos veces.
  const [telon, setTelon] = useState(false);
  useEffect(() => {
    if (session && sessionStorage.getItem(BANDERA_ENTRADA) === "1") {
      sessionStorage.removeItem(BANDERA_ENTRADA);
      setTelon(true);
    }
  }, [session]);
  const cerrarTelon = useCallback(() => setTelon(false), []);
  // telonPendiente cubre el FRAME entre que llega la sesión y corre el efecto:
  // en ese instante telon aún es false y sin esto se alcanzaba a ver un
  // parpadeo de la pantalla de carga (o peor, de la tarjeta de error) antes
  // del telón. Leer sessionStorage en render es inocuo; consumirla no —
  // el removeItem se queda en el efecto.
  const telonPendiente =
    telon ||
    (!!session &&
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(BANDERA_ENTRADA) === "1");
  // `listo` también con error: un telón que nunca se levanta sería peor que
  // ver la tarjeta de error — y con error NO se espera a que haya datos,
  // porque la tarjeta de error es todo lo que va a haber.
  const capaTelon = telonPendiente ? (
    <TelonEntrada
      listo={!!perfil || !!errorPerfil}
      esperarDatos={!errorPerfil}
      onFin={cerrarTelon}
    />
  ) : null;

  // Enlace de invitación o de recuperación: mientras no se defina la
  // contraseña, esto es lo ÚNICO que procede (mismo orden que PILOT). El
  // enlace ya dejó una sesión temporal puesta, pero es solo para eso.
  if (ES_ENLACE_CORREO && !passGuardada) {
    if (session) {
      return (
        <CrearPassword
          invitacion={TIPO_ENLACE === "invite"}
          email={session.user?.email ?? ""}
          onListo={() => setPassGuardada(true)}
        />
      );
    }
    // Sin sesión y ya sin nada que esperar: el enlace venció o ya se usó
    // (Supabase no crea sesión en ese caso). Decirlo, en vez de un login mudo.
    if (!cargando) return <Login mensaje={TEXTO_ENLACE_VENCIDO} />;
    // supabase-js todavía está canjeando el enlace por la sesión temporal
    return (
      <div className="login-split">
        <div className="login-lado">
          <span
            className="spinner"
            style={{ borderColor: "var(--border)", borderTopColor: "var(--s2)" }}
            aria-label="Abriendo tu enlace"
          />
        </div>
      </div>
    );
  }

  // La tele con enlace de dispositivo no necesita sesión (solo lectura, lo cuida la Edge Function).
  if (TOKEN_TELE) {
    return (
      <Suspense fallback={null}>
        <OficinaTele token={TOKEN_TELE} />
      </Suspense>
    );
  }

  // ERROR_ENLACE: el enlace caducado ni siquiera trae `type`, solo el error en
  // el hash — sin esto el login salía mudo y parecía que la contraseña estaba
  // mal, cuando el problema era el enlace.
  if (!session) {
    return <Login mensaje={errorPerfil ?? (ERROR_ENLACE ? TEXTO_ENLACE_VENCIDO : null)} />;
  }

  // Si el perfil no cargó y ya terminó de intentarlo, hay que DECIRLO. Dejar
  // el esqueleto para siempre es exactamente el fallo silencioso que esta
  // migración busca eliminar.
  if (!perfil && !cargando) {
    return (
      <div id="app">
        {capaTelon}
        <main>
          <div className="card" style={{ margin: 24 }}>
            <h3>No se pudo cargar tu perfil</h3>
            <p>{errorPerfil ?? "El servidor no devolvió tu perfil."}</p>
            <button type="button" className="btn" style={{ marginTop: 12, maxWidth: 220 }}
              onClick={() => window.location.reload()}>
              Reintentar
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (cargando || !perfil) {
    // con telón encendido, ÉL es la pantalla de espera: nada de esqueletos
    // parpadeando por debajo
    if (capaTelon) return capaTelon;
    return (
      <div id="app">
        <main>
          <div className="tiles"><EsqueletoTiles n={5} /></div>
        </main>
      </div>
    );
  }

  return (
    <>
      {capaTelon}
    <Routes>
      <Route path="/" element={<Inicio />} />
      <Route path="/oficina/tv" element={<TeleConSesion />} />
      <Route path="/:vista" element={<Pagina />} />
      <Route path="*" element={<Inicio />} />
    </Routes>
    </>
  );
}
